/* ===================================================================
   useFeedbackGate() + useUpdateFeedbackGate() -- private-feedback ROUTING
   config (Brad, 2026-06-17).

   Read:  GET /api/v1/feedback-gate
   Write: PUT /api/v1/feedback-gate

   HARD RULE / FTC: this is PRIVATE-feedback ROUTING only. It NEVER blocks
   or suppresses the public review link. Settings copy keeps that note
   visible. Read hook disabled until a Bearer rr_ token exists.
   =================================================================== */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  api,
  type FeedbackGateResponse,
  type FeedbackGateUpdate,
} from '../lib/api';
import { useApiToken } from './useApiToken';
import { qk } from './queryKeys';

export function useFeedbackGate(): UseQueryResult<FeedbackGateResponse, Error> {
  const hasToken = Boolean(useApiToken());
  return useQuery({
    queryKey: qk.feedbackGate(),
    queryFn: ({ signal }) => api.getFeedbackGate(signal),
    enabled: hasToken,
    staleTime: 60_000,
  });
}

export interface UseUpdateFeedbackGateResult {
  save: (patch: FeedbackGateUpdate) => Promise<FeedbackGateResponse>;
  isPending: boolean;
  error: Error | null;
}

export function useUpdateFeedbackGate(): UseUpdateFeedbackGateResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (patch: FeedbackGateUpdate) => api.updateFeedbackGate(patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.feedbackGate() });
    },
  });
  return {
    save: (patch) => m.mutateAsync(patch),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}
